import { useCallback, useRef, useState } from 'react'

import { ConfirmModal } from '@components/common/modal/ConfirmModal'
import { getClientOpenDocumentCounts } from '../../api/api'
import { Logger } from '../../logger'

const plural = (count, noun) => `${count} open ${noun}${count === 1 ? '' : 's'}`

// "2 open quotes, 1 open work order and 3 open invoices"
const describeDocuments = ({ quotes, workOrders, invoices }) => {
  const parts = [
    quotes > 0 && plural(quotes, 'quote'),
    workOrders > 0 && plural(workOrders, 'work order'),
    invoices > 0 && plural(invoices, 'invoice')
  ].filter(Boolean)
  return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0]
}

const MESSAGES = {
  enable: (documents) => `Turning on the client rebate will add it to ${documents} and update their totals.`,
  disable: (documents) => `Turning off the client rebate will remove it from ${documents} and update their totals.`,
  percent: (documents) => `Changing the rebate percent will recalculate the rebate on ${documents} and update their totals.`
}

/**
 * CAR-3893: a client's rebate toggle or percent edit rewrites the rebate on all of that client's
 * open quotes, work orders and invoices. confirmRebateChange(clientId, 'enable' | 'disable' |
 * 'percent') resolves true straight away when there are none, otherwise shows a confirm modal
 * and resolves with the user's choice. Render rebateConfirmModal in the calling component.
 */
export const useRebateInvoiceConfirm = () => {
  const [pending, setPending] = useState(null)
  const resolverRef = useRef(null)

  const settle = useCallback((proceed) => {
    resolverRef.current?.(proceed)
    resolverRef.current = null
    setPending(null)
  }, [])

  const confirmRebateChange = useCallback(async (clientId, action) => {
    if (!clientId) return true
    let counts
    try {
      const response = await getClientOpenDocumentCounts({ clientId })
      counts = {
        quotes: Number(response?.data?.open_quote_count) || 0,
        workOrders: Number(response?.data?.open_work_order_count) || 0,
        invoices: Number(response?.data?.open_invoice_count) || 0
      }
    } catch (error) {
      // The warning is advisory: a failed lookup must not block the change itself.
      Logger.error({ message: error, payload: { file: 'useRebateInvoiceConfirm', clientId } })
      return true
    }
    if (counts.quotes + counts.workOrders + counts.invoices === 0) return true
    return new Promise((resolve) => {
      resolverRef.current = resolve
      setPending({ counts, action })
    })
  }, [])

  const rebateConfirmModal = pending ? (
    <ConfirmModal
      open={true}
      handleClose={() => settle(false)}
      handleContinue={() => settle(true)}
      title='Update open documents?'
      message={`${MESSAGES[pending.action](describeDocuments(pending.counts))} Closed invoices are not changed.`}
      confirmLabel='Continue'
    />
  ) : null

  return { confirmRebateChange, rebateConfirmModal }
}
